import { useState, useCallback, useEffect } from 'react';
import configurationService from '../services/configurationService';

// Define la estructura inicial y por defecto de las configuraciones
const initialSettings = {
  whatsapp_enabled: false,
  whatsapp_api_url: '',
  whatsapp_api_token: '',
  whatsapp_subscription_queue_id: '',
  whatsapp_dispositivos_queue_id: '',
  whatsapp_default_send_signature: false,
  whatsapp_default_close_ticket: false,
};

/**
 * Hook personalizado para la gestión de la página de configuraciones.
 */
export const useSettings = () => {
  const [settings, setSettings] = useState(initialSettings);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Carga las configuraciones desde el backend.
   */
  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await configurationService.getAllConfigurations();
      console.log('Respuesta del backend:', response); // Debug
      
      if (response && response.success && response.data) {
        // Transforma el array de configuraciones en un objeto clave-valor
        const settingsObject = response.data.reduce((acc, config) => {
          // Convierte 'true'/'false' de string a booleano para los campos que lo necesiten
          if (['whatsapp_enabled', 'whatsapp_default_send_signature', 'whatsapp_default_close_ticket'].includes(config.config_key)) {
            acc[config.config_key] = config.config_value === 'true';
          } else {
            acc[config.config_key] = config.config_value || '';
          }
          return acc;
        }, {});
        setSettings({ ...initialSettings, ...settingsObject });
      } else {
        const errorMessage = response?.message || 'Error al cargar las configuraciones.';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error en loadSettings:', err);
      const errorMessage = err?.message || err?.toString() || 'Error desconocido al cargar las configuraciones.';
      setError(errorMessage);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, []);

  // Carga inicial de datos
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  /**
   * Guarda las configuraciones modificadas en el backend.
   */
  const saveSettings = useCallback(async (newSettings) => {
    setLoading(true);
    setError(null);
    try {
      // Prepara los datos para enviar (convierte booleanos a string si es necesario por el backend)
      const dataToSend = { ...newSettings };
      for (const key in dataToSend) {
        if (typeof dataToSend[key] === 'boolean') {
          dataToSend[key] = String(dataToSend[key]);
        }
      }
      
      const response = await configurationService.updateConfigurations(dataToSend);
      if (!response.success) {
        throw new Error(response.message || 'Error al guardar las configuraciones.');
      }
      // Vuelve a cargar los datos para asegurar consistencia
      await loadSettings();
      return { success: true, message: 'Configuraciones guardadas exitosamente.' };
    } catch (err) {
      console.error(err);
      setError(err.message);
      setLoading(false);
      return { success: false, message: err.message };
    }
  }, [loadSettings]);

  /**
   * Envía un mensaje de prueba.
   */
  const sendTestMessage = useCallback(async (phoneNumber, message, queueType = 'dispositivos') => {
    try {
      const response = await configurationService.testWhatsAppMessage(phoneNumber, message, queueType);
      return response; // Devuelve la respuesta completa de la API
    } catch (err) {
      console.error(err);
      return { success: false, message: err.message };
    }
  }, []);

  return {
    settings,
    setSettings,
    loading,
    initialLoad,
    error,
    loadSettings,
    saveSettings,
    sendTestMessage,
  };
}; 